/**
 * TanStack Query layer for the Fields page's Standard-fields editor ("how we read your
 * fields").
 *
 * - `useStandardFields` — the org's editable standard-field interpretation: each
 *   value-classifiable field's family vocabulary + stored per-value classifications, and
 *   each percentConfigurable field's resolved percent variant.
 * - `useSaveClassifications` — batch-upsert the value classifications. On success it
 *   invalidates the standard-fields read (the saved vocabulary) and, because a real
 *   vocabulary change re-derives the org, re-arms the block-until-settled modeling state.
 * - `useSetPercentVariant` — set a percentConfigurable field's percent variant. A change
 *   that crosses the percent boundary re-projects the field's loans, so it invalidates
 *   the standard-fields read, the portfolio loans + summary, and the modeling state.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  portfolioStandardFieldsContract,
  portfolioFieldClassificationsContract,
  portfolioStandardPercentVariantContract,
  type StandardFieldsResponse,
} from '@repo/shared/common';
import { contractQueryOptions, createMutationHook, MODELING_STATUS_KEY } from '~/lib/query';
import { PORTFOLIO_KEY, portfolioKeys } from './use-portfolio';

/** Prefix key matching every loan-list cache entry (a percent re-scale moves the list). */
const loansPrefixKey = [...PORTFOLIO_KEY, 'loans'] as const;

/** Query key for the standard-fields editor read — shared by the loader and the hook. */
export const standardFieldsKey = [...PORTFOLIO_KEY, 'standard-fields'] as const;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- opaque queryOptions brand; the contract keeps callers typed.
export function standardFieldsQueryOptions() {
  return contractQueryOptions(portfolioStandardFieldsContract, [...standardFieldsKey]);
}

/** The org's editable standard-field interpretation (vocabulary + percent variant). */
export function useStandardFields(): UseQueryResult<StandardFieldsResponse> {
  return useQuery(standardFieldsQueryOptions());
}

/**
 * Batch-upsert value classifications for classifiable standard fields. A real vocabulary
 * change re-derives the org (derive-only), so re-arm the modeling block-until-settled
 * state; the read refreshes so the editor reflects the saved vocabulary.
 */
export const useSaveClassifications = createMutationHook(portfolioFieldClassificationsContract, [
  standardFieldsKey,
  MODELING_STATUS_KEY,
]);

/**
 * Set a percentConfigurable standard field's percent variant. A boundary-crossing change
 * re-scales stored values and re-projects the field's loans, so invalidate the read, the
 * loan list + summary, and re-arm the modeling state.
 */
export const useSetPercentVariant = createMutationHook(portfolioStandardPercentVariantContract, [
  standardFieldsKey,
  loansPrefixKey,
  portfolioKeys.summary,
  MODELING_STATUS_KEY,
]);
